/**
 * Visitas propias (pantalla Visitas del admin, tabla web_visitas en Supabase): cada página, cada
 * pestaña y cuánto tiempo estuvo en cada página. Se llama desde lib/analytics.ts (las mismas
 * páginas y eventos que Google Analytics), así ninguna pantalla tiene que hacer nada.
 * El servidor completa IP, ciudad, dispositivo, de dónde llegó y, si entró con PIN, quién es.
 * No se mide: el admin (admin_token en el navegador) ni localhost (salvo visitas_debug=1).
 */
const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
const URL_VISITAS = `${BACKEND_URL}/api/visitas`;
const SESION_MIN = 30;   // una visita termina tras 30 minutos sin actividad

function activo(): boolean {
  try {
    if (localStorage.getItem('admin_token')) return false;
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) return localStorage.getItem('visitas_debug') === '1';
  } catch { return false; }
  return true;
}

function nuevoId(): string {
  try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
}

function visitante(): string {
  try {
    let v = localStorage.getItem('vis_id');
    if (!v) { v = nuevoId(); localStorage.setItem('vis_id', v); }
    return v;
  } catch { return 'sin-storage'; }
}

/** La visita en curso; `nueva` si recién empieza (para guardar de dónde llegó). */
function sesion(): { id: string; nueva: boolean } {
  try {
    const s = JSON.parse(localStorage.getItem('vis_sesion') || 'null');
    const ahora = Date.now();
    if (s && ahora - s.t < SESION_MIN * 60000) {
      localStorage.setItem('vis_sesion', JSON.stringify({ id: s.id, t: ahora }));
      return { id: s.id, nueva: false };
    }
    const id = nuevoId();
    localStorage.setItem('vis_sesion', JSON.stringify({ id, t: ahora }));
    return { id, nueva: true };
  } catch { return { id: 'sin-storage', nueva: false }; }
}

function tokenJugador(): string | undefined {
  try { return JSON.parse(localStorage.getItem('jugador_session') || 'null')?.token || undefined; } catch { return undefined; }
}

function enviar(datos: Record<string, any>, sesionInfo = sesion(), beacon = false) {
  const body = JSON.stringify({ ...datos, sesion: sesionInfo.id, visitante: visitante(), token: tokenJugador() });
  try {
    if (beacon && navigator.sendBeacon) { navigator.sendBeacon(URL_VISITAS, new Blob([body], { type: 'application/json' })); return; }
    fetch(URL_VISITAS, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* nunca rompe la app */ }
}

let actual: { ruta: string; pagina: string; desde: number } | null = null;
let pausada: { ruta: string; pagina: string } | null = null;
let scrollMax = 0;   // hasta dónde bajó en la página actual (%)

function cerrarPagina(beacon: boolean) {
  if (!actual) return;
  const s = Math.round((Date.now() - actual.desde) / 1000);
  if (s >= 1) enviar({ tipo: 'salida', ruta: actual.ruta, pagina: actual.pagina, duracion_s: s,
                       detalle: scrollMax ? { scroll: scrollMax } : undefined }, sesion(), beacon);
  actual = null;
}

/** Una página nueva (cambio de ruta). */
export function visitaPagina(ruta: string, pagina: string) {
  if (!activo()) return;
  cerrarPagina(false);
  pausada = null;
  const s = sesion();
  const detalle: Record<string, string> = {};
  try {
    new URLSearchParams(window.location.search).forEach((v, k) => { if (k.startsWith('utm_')) detalle[k] = v; });
  } catch { /* sin parámetros */ }
  scrollMax = 0;
  enviar({ tipo: 'pagina', ruta, pagina, entrada: s.nueva, referrer: s.nueva ? document.referrer || undefined : undefined,
           detalle: Object.keys(detalle).length ? detalle : undefined }, s);
  actual = { ruta, pagina, desde: Date.now() };
}

/** Un evento (los mismos de Google Analytics); si trae una pestaña, se guarda aparte. */
export function visitaEvento(evento: string, params?: Record<string, any>) {
  if (!activo()) return;
  const pestana = params?.tab_nombre ?? params?.tab ?? params?.pestana;
  enviar({ tipo: 'evento', evento, pestana: pestana != null ? String(pestana) : undefined,
           pagina: actual?.pagina, ruta: actual?.ruta, detalle: params && Object.keys(params).length ? params : undefined });
}

// ── Rastros automáticos: toques, scroll y errores (ninguna pantalla tiene que hacer nada) ──
let ultimoToque = { k: '', t: 0 };
let errores = 0;

function textoDe(el: Element): string {
  const t = el.getAttribute('aria-label') || el.getAttribute('title') || (el as HTMLElement).innerText || el.textContent || '';
  return t.replace(/\s+/g, ' ').trim().slice(0, 60);
}

if (typeof document !== 'undefined') {
  // Cada toque en un botón o enlace: su texto y a dónde lleva
  document.addEventListener('click', (e) => {
    if (!activo()) return;
    const el = (e.target as Element | null)?.closest?.('a, button, [role="button"], [role="tab"], summary, select');
    if (!el) return;
    const texto = textoDe(el);
    const href = el.tagName === 'A' ? (el as HTMLAnchorElement).getAttribute('href') || '' : '';
    if (!texto && !href) return;
    const k = texto + '|' + href;
    if (k === ultimoToque.k && Date.now() - ultimoToque.t < 700) return;   // doble toque
    ultimoToque = { k, t: Date.now() };
    enviar({ tipo: 'evento', evento: 'clic', pagina: actual?.pagina, ruta: actual?.ruta,
             detalle: { texto: texto || undefined, destino: href ? href.slice(0, 200) : undefined } });
  }, true);

  // Hasta dónde bajó (se manda junto con el tiempo de la página)
  let pendiente = false;
  window.addEventListener('scroll', () => {
    if (pendiente) return;
    pendiente = true;
    window.setTimeout(() => {
      pendiente = false;
      const d = document.documentElement;
      const alto = Math.max(1, d.scrollHeight - window.innerHeight);
      scrollMax = Math.max(scrollMax, Math.min(100, Math.round((window.scrollY / alto) * 100)));
    }, 400);
  }, { passive: true });

  // Errores de la app en el navegador de la persona (máximo 5 por página cargada)
  window.addEventListener('error', (ev) => {
    if (!activo() || errores >= 5) return;
    errores++;
    enviar({ tipo: 'evento', evento: 'error_js', pagina: actual?.pagina, ruta: actual?.ruta,
             detalle: { mensaje: String(ev.message || '').slice(0, 300), origen: `${String(ev.filename || '').split('/').pop()}:${ev.lineno || 0}` } });
  });
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    if (!activo() || errores >= 5) return;
    const m = String((ev.reason && (ev.reason.message || ev.reason)) || '');
    if (/Network Error|timeout|abort|401|403|404/i.test(m)) return;   // errores de red: no son fallas de la app
    errores++;
    enviar({ tipo: 'evento', evento: 'error_js', pagina: actual?.pagina, ruta: actual?.ruta, detalle: { mensaje: m.slice(0, 300), origen: 'promesa' } });
  });
}

// En el celular la app pasa a segundo plano (otra app, pantalla apagada): se cierra el tiempo de la
// página con un envío que sobrevive al cierre; al volver se sigue contando sin una página nueva.
if (typeof document !== 'undefined') {
  // Cerrar la pestaña, recargar o irse a otro sitio: también cierra el tiempo de la página
  window.addEventListener('pagehide', () => { if (activo()) cerrarPagina(true); });
  document.addEventListener('visibilitychange', () => {
    if (!activo()) return;
    if (document.visibilityState === 'hidden') {
      if (actual) { pausada = { ruta: actual.ruta, pagina: actual.pagina }; cerrarPagina(true); }
    } else if (pausada && !actual) {
      actual = { ...pausada, desde: Date.now() };
      pausada = null;
      sesion();   // mantiene viva la visita
    }
  });
}
